import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { format } from 'date-fns';

import { CONFIG } from '~/config';

import { ExternalRestClient } from '~/modules/requests';

import { BOOKING_CONTEXT, PAGE_SIZE } from '~/literals';

import { FlightSearchParams, Offer } from '~/types';

import FlightCard from './FlightCard';
import FlightCardItemDetailsModal from './FlightCardItemDetailsModal';
import FlightItinerarySummaryCard from './FlightItinerarySummaryCard';

export default function ItineraryFlightSelection({
  onBack,
  onSelect,
  searchParams,
}: {
  searchParams: FlightSearchParams;
  onSelect: (offer: Offer) => void;
  onBack: () => void;
}) {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [detailsOffer, setDetailsOffer] = useState<Offer | null>(null);
  const [visibleOfferCount, setVisibleOfferCount] = useState(PAGE_SIZE);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const visibleOffers = offers.slice(0, visibleOfferCount);
  const hasMoreOffers = visibleOfferCount < offers.length;

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError('');
    setOffers([]);
    setVisibleOfferCount(PAGE_SIZE);
    const params = new URLSearchParams({
      source: 'FX_PORT',
      bookingContext: BOOKING_CONTEXT.FLIGHT_ITINERARY,
      from: searchParams.from?.iata || '',
      to: searchParams.to?.iata || '',
      adults: String(searchParams.passengers.adults),
      children: String(searchParams.passengers.children),
      infants: String(searchParams.passengers.infants),
      departureDate: format(new Date(searchParams.departureDate!), 'yyyy-MM-dd'),
      flightType: searchParams.type,
      currencyCode: searchParams.currencyCode,
      travelClass:
        (
          {
            ECONOMY: 'Economy',
            BUSINESS: 'Business',
            FIRSTCLASS: 'First',
            PREMIUM_ECONOMY: 'Premium Economy',
          } as Record<string, string>
        )[searchParams.travelClass] || searchParams.travelClass,
    });

    if (searchParams.type === 'return' && searchParams.returnDate) {
      params.set('returnDate', format(new Date(searchParams.returnDate), 'yyyy-MM-dd'));
    }

    ExternalRestClient.get<{ offers: Offer[] }>(
      `${CONFIG.env.apiBaseUrl}v1/flight-booking/offers?${params}`,
    )
      .then(response => {
        if (!active) {
          return;
        }

        if (response.status !== 200 || !Array.isArray(response.data?.offers)) {
          throw new Error('SEARCH_FAILED');
        }

        setOffers(response.data.offers.filter(offer => offer.searchId && offer.offerId));
      })
      .catch(() => {
        if (active) {
          setError('Flights could not be loaded. Please retry or change your search.');
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [searchParams, attempt]);

  useEffect(() => {
    if (!hasMoreOffers || !loadMoreRef.current) {
      return undefined;
    }

    if (!('IntersectionObserver' in window)) {
      setVisibleOfferCount(offers.length);

      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          setVisibleOfferCount(count => Math.min(count + PAGE_SIZE, offers.length));
        }
      },
      { rootMargin: '300px 0px', threshold: 0 },
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasMoreOffers, offers.length, visibleOfferCount]);

  return (
    <Box>
      <FlightItinerarySummaryCard onEdit={onBack} searchParams={searchParams} />
      {detailsOffer ? (
        <FlightCardItemDetailsModal
          isSearchPage
          offer={detailsOffer}
          onClose={() => setDetailsOffer(null)}
          open
          passengerPricing={detailsOffer.passengerPricing}
        />
      ) : null}
      <Typography gutterBottom variant="h6">
        Select a flight for your itinerary
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        You are purchasing an itinerary document, not an airline ticket. Your selected package price
        applies.
      </Alert>
      {loading ? (
        <Box
          aria-label="Searching flights"
          role="status"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      ) : null}
      {error ? (
        <Alert
          action={<Button onClick={() => setAttempt(value => value + 1)}>Retry</Button>}
          severity="error"
        >
          {error}
        </Alert>
      ) : null}
      {!loading && !error && !offers.length ? (
        <Alert severity="info">No flights found. Please change your dates or route.</Alert>
      ) : null}
      {visibleOffers.map(offer => (
        <FlightCard
          key={`${offer.searchId}:${offer.offerId}`}
          bookingContext={BOOKING_CONTEXT.FLIGHT_ITINERARY}
          isSearchPage
          offer={offer}
          onBookButtonClicked={() => onSelect(offer)}
          onDetailsButtonClicked={setDetailsOffer}
          selectionOnly
        />
      ))}
      {hasMoreOffers ? (
        <Box
          ref={loadMoreRef}
          aria-label="Loading more flights"
          role="status"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 80,
            py: 2,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : null}
    </Box>
  );
}
